import {
  Stack,
  TextField,
  useTheme,
  Button,
  Container,
  Typography,
  Paper,
  Grid,
  Dialog,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  DialogTitle,
  DialogActions,
  Snackbar,
  Alert,
  LinearProgress,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Box,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Badge,
  Tooltip,
  Fade,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  InputAdornment,
  MenuItem,
  Select,
  Avatar,
  DialogContent,
  Zoom,
  Autocomplete,
  Popover,
  Switch,
  FormGroup,
} from '@mui/material';
import Slide from '@mui/material/Slide';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import styled from '@emotion/styled';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import ImageIcon from '@mui/icons-material/Image';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import imagepreview from '../../media/bannerup.png';
import NoimageFound from '../../media/noimage.jpg';
import axios from '../../axios';
import './animation.css';

const useStyles = styled((theme) => ({
  fileInput: {
    display: 'none',
  },
}));

// Header styled component - matching all masters
const HeaderPaper = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)',
  borderRadius: '12px',
  padding: '16px 24px',
  marginBottom: '16px',
  boxShadow: '0 4px 20px rgba(19, 78, 94, 0.3)',
}));

// Compact Card styled component
const CompactCard = styled(Card)(({ theme }) => ({
  borderRadius: '10px',
  transition: 'all 0.3s ease',
  border: '1px solid #e8ecf0',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
    '& .card-overlay': {
      opacity: 1,
    },
  },
}));

const CardOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  background: 'rgba(0,0,0,0.45)',
  opacity: 0,
  transition: 'opacity 0.3s ease',
  backdropFilter: 'blur(3px)',
  zIndex: 2,
  pointerEvents: 'none',
  '& .MuiIconButton-root': {
    pointerEvents: 'auto',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: '#fff',
  fontWeight: 700,
  textAlign: 'left',
  backgroundColor: '#1a3a4a',
  padding: '8px 12px',
  whiteSpace: 'nowrap',
  fontSize: '0.75rem',
  letterSpacing: '0.03em',
  borderBottom: '2px solid rgba(255,255,255,0.1)',
  position: 'sticky',
  top: 0,
  zIndex: 10,
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.04)',
    transition: 'background-color 0.2s ease',
  },
}));

const StickyTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: '70vh',
  '& .MuiTableHead-root': {
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
}));

// Fullscreen Image Preview Dialog with transparent background
const FullscreenImageDialog = ({ open, imageUrl, onClose, title }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.92)',
          backdropFilter: 'blur(8px)',
          boxShadow: 'none',
          borderRadius: 0,
        },
      }}
    >
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          p: 4,
          position: 'relative',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            color: '#fff',
            bgcolor: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(4px)',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.25)',
            },
            zIndex: 10,
          }}
        >
          <CloseIcon sx={{ fontSize: 32 }} />
        </IconButton>

        {title && (
          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              position: 'absolute',
              top: 20,
              left: 50,
              fontWeight: 600,
              zIndex: 10,
            }}
          >
            {title}
          </Typography>
        )}

        <Zoom in={open} timeout={400}>
          <Box
            component="img"
            src={imageUrl || NoimageFound}
            alt="Preview"
            sx={{
              maxWidth: '95%',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: 2,
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}
          />
        </Zoom>

        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.4)',
            mt: 3,
            letterSpacing: '0.1em',
          }}
        >
          Click anywhere outside the image or press ESC to close
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

const ClientReviews = () => {
  const tokent =
    'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJldmVudGxpc3QiOlt7IlVzZXJJRCI6IjEiLCJMb2dpbkNvZGUiOiIwMSIsIkxvZ2luTmFtZSI6IkFkbWluIiwiRW1haWxJZCI6ImFkbWluQGdtYWlsLmNvbSIsIlVzZXJUeXBlIjoiQURNSU4ifV0sImlhdCI6MTYzODM1NDczMX0.ZW6zEHIXTxfT-QWEzS6-GuY7bRupf2Jc_tp4fXIRabQ';
  const theme = useTheme();
  const classes = useStyles();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(false);
  const [openAlert, setopenAlert] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [operation, setOperation] = useState('Add');
  const [alertType, setAlertType] = useState('success');
  const [alertMessage, setAlertMessage] = useState('');
  const [openLoader, setOpenLoader] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  // Filter states using Popover
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [companyFilter, setCompanyFilter] = useState(null);

  // Fullscreen image preview state
  const [previewImageOpen, setPreviewImageOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [previewImageTitle, setPreviewImageTitle] = useState('');

  const [CompanyName, setCompanyName] = useState('');
  const [CompanyLogo, setCompanyLogo] = useState(null);
  const [UserName, setUserName] = useState('');
  const [Designation, setDesignation] = useState('');
  const [selectedImageFile, setSelectedImageFile] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [existingImageName, setExistingImageName] = useState('');
  const [Review, setReview] = useState('');
  const [VideoURL, setVideoURL] = useState('');
  const [ActiveStatus, setActiveStatus] = useState('Y');

  const [reviewData, setreviewData] = useState([]);
  const [IdToDelete, setIdToDelete] = useState(null);
  const [editID, setEditID] = useState();

  const [errors, setErrors] = useState({
    CompanyName: false,
    CompanyLogo: false,
    UserName: false,
    Designation: false,
    Review: false,
    VideoURL: false,
    ActiveStatus: false,
    selectedImageFile: false,
  });

  const [helperTexts, setHelperTexts] = useState({
    CompanyName: '',
    CompanyLogo: '',
    UserName: '',
    Designation: '',
    Review: '',
    VideoURL: '',
    ActiveStatus: '',
    selectedImageFile: '',
  });

  useEffect(() => {
    getAllReview();
  }, []);

  const getAllReview = async () => {
    setOpenLoader(true);
    try {
      const res = await axios.instance.get('/getAllReview', {
        headers: { Authorization: tokent, 'Content-Type': 'application/json' },
      });
      setreviewData(res.data);
      setOpenLoader(false);
      console.log('Reviews', res.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setOpenLoader(false);
    }
  };

  const InsertReview = async (newUser) => {
    try {
      await axios.instance.post('/InsertReview', newUser, {
        headers: {
          Authorization: tokent,
          'Content-Type': 'multipart/form-data',
        },
      });
      getAllReview();
      setAlertType('success');
      setAlertMessage('New Review Added Successfully!');
      setopenAlert(true);
    } catch (error) {
      console.error('Error adding Review:', error);
      setAlertType('error');
      setAlertMessage('Failed to add the Review.');
      setopenAlert(true);
    }
  };

  const UpdateReview = async (userId, updatedUser) => {
    try {
      await axios.instance.put(`/UpdateReview/${userId}`, updatedUser, {
        headers: {
          Authorization: tokent,
          'Content-Type': 'multipart/form-data',
        },
      });
      setopenAlert(true);
      setEditID();
      getAllReview();
      setAlertType('success');
      setAlertMessage('Review Updated Successfully!');
    } catch (error) {
      console.error('Error updating Review:', error);
      setAlertType('error');
      setAlertMessage('Failed to update the Review.');
      setopenAlert(true);
    }
  };

  const DeleteReview = async (userId) => {
    try {
      await axios.instance
        .delete(`/DeleteReview/${userId}`, {
          headers: { Authorization: tokent, 'Content-Type': 'application/json' },
        })
        .then((res) => {
          if (res.data === '') {
            getAllReview();
            setOpenDelete(false);
            setAlertType('warning');
            setAlertMessage('Review Deleted Successfully!');
            setopenAlert(true);
          } else {
            getAllReview();
            setOpenDelete(false);
            setAlertType('error');
            setAlertMessage("Oops! Can't delete this data. It's connected to other information.");
            setopenAlert(true);
          }
        });
    } catch (error) {
      console.error('Error deleting Review:', error);
      setAlertType('error');
      setAlertMessage('Failed to delete the Review.');
      setopenAlert(true);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setErrors((prev) => ({ ...prev, selectedImageFile: false }));
  };

  const resetFields = () => {
    setCompanyName('');
    setCompanyLogo(null);
    setDesignation('');
    setUserName('');
    setReview('');
    setVideoURL('');
    setActiveStatus('Y');
    setSelectedImageFile('');
    setExistingImageName('');
    setImagePreview('');
    setErrors({
      CompanyName: false,
      CompanyLogo: false,
      UserName: false,
      Designation: false,
      Review: false,
      VideoURL: false,
      ActiveStatus: false,
      selectedImageFile: false,
    });
    setHelperTexts({
      CompanyName: '',
      CompanyLogo: '',
      UserName: '',
      Designation: '',
      Review: '',
      VideoURL: '',
      ActiveStatus: '',
      selectedImageFile: '',
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const inputValidation = !selectedImageFile || !CompanyName || !UserName || !Designation || !Review || !ActiveStatus;

    if (inputValidation) {
      if (!selectedImageFile) {
        setErrors((prev) => ({ ...prev, selectedImageFile: true }));
        setHelperTexts((prev) => ({ ...prev, selectedImageFile: 'Please upload company logo' }));
      }
      if (!CompanyName) {
        setErrors((prev) => ({ ...prev, CompanyName: true }));
        setHelperTexts((prev) => ({ ...prev, CompanyName: 'Please enter company name' }));
      }
      if (!UserName) {
        setErrors((prev) => ({ ...prev, UserName: true }));
        setHelperTexts((prev) => ({ ...prev, UserName: 'Please enter user name' }));
      }
      if (!Designation) {
        setErrors((prev) => ({ ...prev, Designation: true }));
        setHelperTexts((prev) => ({ ...prev, Designation: 'Please enter designation' }));
      }
      if (!Review) {
        setErrors((prev) => ({ ...prev, Review: true }));
        setHelperTexts((prev) => ({ ...prev, Review: 'Please enter review' }));
      }
      if (!ActiveStatus) {
        setErrors((prev) => ({ ...prev, ActiveStatus: true }));
        setHelperTexts((prev) => ({ ...prev, ActiveStatus: 'Please select status' }));
      }
      return;
    }

    const formData = new FormData();
    const imageValue =
      selectedImageFile instanceof File
        ? selectedImageFile
        : operation === 'Edit'
        ? existingImageName || selectedImageFile || ''
        : selectedImageFile || '';

    formData.append('LogoFileName', imageValue);
    formData.append('CompanyName', CompanyName);
    formData.append('UserName', UserName);
    formData.append('Designation', Designation);
    formData.append('Review', Review);
    formData.append('URL', VideoURL || '');
    formData.append('ActiveStatus', ActiveStatus);
    formData.append(operation === 'Edit' ? 'ModifyedBy' : 'CreatedBy', 5);

    if (operation === 'Add') {
      InsertReview(formData);
    } else {
      UpdateReview(editID, formData);
    }
    resetFields();
    handleClose();
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'CompanyName') setCompanyName(value);
    if (name === 'UserName') setUserName(value);
    if (name === 'Designation') setDesignation(value);
    if (name === 'Review') setReview(value);
    if (name === 'VideoURL') setVideoURL(value);
    if (name === 'ActiveStatus') setActiveStatus(value);
    validateInput(name, value);
  };

  const validateInput = (name, value) => {
    let error = false;
    let helperText = '';
    const nameRegex = /^[A-Za-z\s]+$/;

    if (name === 'CompanyName') {
      if (!value.trim()) {
        error = true;
        helperText = 'Field cannot be empty';
      }
    } else if (name === 'UserName') {
      if (!value.trim()) {
        error = true;
        helperText = 'Field cannot be empty';
      } else if (!nameRegex.test(value)) {
        error = true;
        helperText = 'Please enter a valid name';
      }
    } else if (name === 'Designation') {
      if (!value.trim()) {
        error = true;
        helperText = 'Field cannot be empty';
      }
    } else if (name === 'Review') {
      if (!value.trim()) {
        error = true;
        helperText = 'Field cannot be empty';
      }
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
    setHelperTexts((prev) => ({ ...prev, [name]: helperText }));
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    if (value && value.length > 0) {
      validateInput(name, value);
    }
  };

  const handleAddOpen = () => {
    resetFields();
    setOpen(true);
    setOperation('Add');
  };

  const handleEdit = (user) => {
    resetFields();
    setErrors({
      CompanyName: false,
      CompanyLogo: false,
      UserName: false,
      Designation: false,
      Review: false,
      VideoURL: false,
      ActiveStatus: false,
      selectedImageFile: false,
    });
    setHelperTexts({
      CompanyName: '',
      CompanyLogo: '',
      UserName: '',
      Designation: '',
      Review: '',
      VideoURL: '',
      ActiveStatus: '',
      selectedImageFile: '',
    });
    setEditID(user.ReviewID);
    setCompanyName(user.CompanyName);
    setUserName(user.UserName);
    const imageName = user.LogoFileName || '';
    const imageUrl = imageName ? `${axios.baseURL}ClientLogos/${imageName}` : '';
    setExistingImageName(imageName);
    setSelectedImageFile(imageName);
    setImagePreview(imageUrl);
    setDesignation(user.Designation);
    setReview(user.Review);
    setVideoURL(user.URL || '');
    setActiveStatus(user.ActiveStatus);
    setOpen(true);
    setOperation('Edit');
  };

  const handleDelete = (courseId) => {
    setOpenDelete(true);
    setIdToDelete(courseId);
  };

  const handleDeleteConfirmed = () => {
    DeleteReview(IdToDelete);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
  };

  const handleCloseAlert = () => {
    setopenAlert(false);
  };

  const handleClose = () => {
    resetFields();
    setOpen(false);
  };

  const handleImagePreview = (imageUrl, title) => {
    if (!imageUrl || imageUrl === NoimageFound) {
      return;
    }
    setPreviewImageUrl(imageUrl);
    setPreviewImageTitle(title || 'Image Preview');
    setPreviewImageOpen(true);
  };

  const handleCloseImagePreview = () => {
    setPreviewImageOpen(false);
    setPreviewImageUrl('');
    setPreviewImageTitle('');
  };

  // Filter functions with Popover
  const handleFilterOpen = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const getFilteredData = () => {
    let filtered = reviewData;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        Object.values(item).some((value) => typeof value === 'string' && value.toLowerCase().includes(searchLower))
      );
    }

    if (activeFilter && activeFilter.value !== 'all') {
      filtered = filtered.filter((item) => item.ActiveStatus === activeFilter.value);
    }

    if (companyFilter && companyFilter.value !== 'all') {
      filtered = filtered.filter((item) => item.CompanyName?.toLowerCase() === companyFilter.value.toLowerCase());
    }

    return filtered;
  };

  const filteredData = getFilteredData();

  const clearAllFilters = () => {
    setActiveFilter(null);
    setCompanyFilter(null);
    setSearchTerm('');
    handleFilterClose();
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (activeFilter && activeFilter.value !== 'all') count += 1;
    if (companyFilter && companyFilter.value !== 'all') count += 1;
    if (searchTerm) count += 1;
    return count;
  };

  const getUniqueCompanies = () => {
    const companies = reviewData.filter((item) => item.CompanyName).map((item) => item.CompanyName);
    return [...new Set(companies)];
  };

  // Status options for Autocomplete
  const statusOptions = [
    { label: 'All Status', value: 'all' },
    { label: 'Active', value: 'Y' },
    { label: 'Inactive', value: 'N' },
  ];

  // Company options for Autocomplete
  const companyOptions = [
    { label: 'All Companies', value: 'all' },
    ...getUniqueCompanies().map((company) => ({ label: company, value: company })),
  ];

  // Render compact card view - no pagination
  const renderCardView = () => {
    return (
      <Grid container spacing={2}>
        {filteredData.map((user) => {
          const imageUrl = user.LogoFileName ? `${axios.baseURL}ClientLogos/${user.LogoFileName}` : NoimageFound;
          return (
            <Grid item xs={6} sm={4} md={3} lg={2} key={user.ReviewID}>
              <Fade in timeout={400}>
                <CompactCard>
                  {/* Image section - click to preview */}
                  <Box
                    sx={{
                      height: 120,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                      p: 1.5,
                      position: 'relative',
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      // Don't trigger if clicking on overlay buttons
                      if (!e.target.closest('.MuiIconButton-root')) {
                        handleImagePreview(imageUrl, user.CompanyName);
                      }
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt={user.CompanyName}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100px',
                        objectFit: 'contain',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(0,0,0,0.15)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        '&:hover': {
                          opacity: 1,
                        },
                        pointerEvents: 'none',
                      }}
                    >
                      <ImageIcon sx={{ color: '#fff', fontSize: 32 }} />
                    </Box>
                  </Box>

                  {/* Status Chip */}
                  <Chip
                    label={user.ActiveStatus === 'Y' ? 'Active' : 'Inactive'}
                    color={user.ActiveStatus === 'Y' ? 'success' : 'error'}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      fontWeight: 600,
                      zIndex: 3,
                      fontSize: '0.55rem',
                      height: 18,
                    }}
                  />

                  {/* Overlay with actions */}
                  <CardOverlay className="card-overlay">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(user);
                        }}
                        sx={{
                          color: '#fff',
                          bgcolor: 'rgba(255,255,255,0.2)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(user.ReviewID);
                        }}
                        sx={{
                          color: '#fff',
                          bgcolor: 'rgba(255,255,255,0.2)',
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                        }}
                      >
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardOverlay>

                  {/* User Info */}
                  <Box
                    sx={{
                      p: 1,
                      borderTop: '1px solid #e8ecf0',
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {user.UserName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.6rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {user.CompanyName}
                    </Typography>
                  </Box>
                </CompactCard>
              </Fade>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  // Render compact table view - with sticky header
  const renderTableView = () => {
    return (
      <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #e8ecf0' }}>
        <StickyTableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell align="center" sx={{ width: 40 }}>
                  #
                </StyledTableCell>
                <StyledTableCell>Reviewer</StyledTableCell>
                <StyledTableCell>Review</StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 80 }}>
                  Status
                </StyledTableCell>
                <StyledTableCell align="center" sx={{ width: 90 }}>
                  Actions
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((user, index) => {
                const imageUrl = user.LogoFileName ? `${axios.baseURL}ClientLogos/${user.LogoFileName}` : NoimageFound;
                return (
                  <StyledTableRow key={user.ReviewID} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 500, py: 1 }}>
                      {index + 1}
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#f5f5f5',
                            borderRadius: 1,
                            p: 0.5,
                            flexShrink: 0,
                            cursor: 'pointer',
                          }}
                          onClick={() => handleImagePreview(imageUrl, user.CompanyName)}
                        >
                          <img
                            src={imageUrl}
                            alt={user.CompanyName}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
                            {user.UserName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            {user.Designation} • {user.CompanyName}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.75rem',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontStyle: 'italic',
                          maxWidth: 200,
                        }}
                      >
                        "{user.Review}"
                      </Typography>
                      {user.URL && (
                        <Chip
                          icon={<VideoLibraryIcon sx={{ fontSize: 12 }} />}
                          label="Video"
                          size="small"
                          sx={{ mt: 0.5, height: 18, fontSize: '0.6rem' }}
                          color="warning"
                        />
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Chip
                        icon={
                          user.ActiveStatus === 'Y' ? (
                            <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <HighlightOffIcon sx={{ fontSize: 14 }} />
                          )
                        }
                        label={user.ActiveStatus === 'Y' ? 'Active' : 'Inactive'}
                        color={user.ActiveStatus === 'Y' ? 'success' : 'error'}
                        size="small"
                        sx={{ fontWeight: 600, height: 22, fontSize: '0.65rem' }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(user)}
                          sx={{ color: 'primary.main', p: 0.5 }}
                        >
                          <EditIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(user.ReviewID)}
                          sx={{ color: 'error.main', p: 0.5 }}
                        >
                          <DeleteForeverIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </StyledTableRow>
                );
              })}
            </TableBody>
          </Table>
        </StickyTableContainer>
      </Paper>
    );
  };

  return (
    <>
      <style>{`
        /* Sticky Dialog Styles */
        .sticky-dialog .MuiDialog-paper {
          display: flex;
          flex-direction: column;
          max-height: 95vh;
        }
        .sticky-dialog .MuiDialogContent-root {
          flex: 1 1 auto;
          overflow-y: auto;
          padding: 24px;
        }
        .sticky-dialog .MuiDialogActions-root {
          flex-shrink: 0;
        }
        .sticky-dialog .MuiDialogTitle-root {
          flex-shrink: 0;
        }
      `}</style>

      <Snackbar
        open={openAlert}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert onClose={handleCloseAlert} severity={alertType} variant="filled" sx={{ width: '100%', borderRadius: 2 }}>
          {alertMessage}
        </Alert>
      </Snackbar>

      <Container maxWidth="xl" sx={{ pt: 3, pb: 4 }}>
        {/* Header */}
        <HeaderPaper elevation={0}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <FormatQuoteIcon sx={{ color: 'white' }} />
              <Typography variant="h6" fontWeight={700} color="white" sx={{ letterSpacing: '0.02em' }}>
                Client Reviews
              </Typography>
            </Stack>
            <Typography variant="caption" color="rgba(255,255,255,0.7)">
              Total: {reviewData.length} reviews
            </Typography>
          </Stack>
        </HeaderPaper>

        {/* Search & Actions Bar */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          sx={{ mb: 3 }}
        >
          <TextField
            placeholder="Search reviews..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: { md: 200 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" spacing={1} alignItems="center">
            <Badge badgeContent={getActiveFilterCount()} color="primary" invisible={getActiveFilterCount() === 0}>
              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={handleFilterOpen}
                size="small"
                sx={{ borderRadius: 2 }}
              >
                Filters
              </Button>
            </Badge>

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, val) => val && setViewMode(val)}
              size="small"
              sx={{ '& .MuiToggleButton-root': { px: 1.5 } }}
            >
              <ToggleButton value="grid">
                <ViewModuleIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="table">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="contained"
              startIcon={<PlaylistAddIcon />}
              onClick={handleAddOpen}
              sx={{ borderRadius: 2, boxShadow: 2 }}
            >
              New Review
            </Button>
          </Stack>
        </Stack>

        {/* Filter Popover */}
        <Popover
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={handleFilterClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              maxWidth: 320,
              width: '100%',
              p: 2,
            },
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Filter Reviews
          </Typography>

          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              size="small"
              options={statusOptions}
              getOptionLabel={(option) => option.label}
              value={activeFilter}
              onChange={(e, newValue) => setActiveFilter(newValue)}
              renderInput={(params) => <TextField {...params} label="Status" size="small" />}
            />

            <Autocomplete
              size="small"
              options={companyOptions}
              getOptionLabel={(option) => option.label}
              value={companyFilter}
              onChange={(e, newValue) => setCompanyFilter(newValue)}
              renderInput={(params) => <TextField {...params} label="Company" size="small" />}
            />

            <Stack direction="row" spacing={1}>
              <Button variant="contained" size="small" fullWidth onClick={clearAllFilters} startIcon={<ClearIcon />}>
                Clear All
              </Button>
              <Button variant="outlined" size="small" fullWidth onClick={handleFilterClose}>
                Apply
              </Button>
            </Stack>
          </Stack>
        </Popover>

        {/* Results count */}
        {filteredData.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Showing {filteredData.length} review{filteredData.length !== 1 ? 's' : ''}
          </Typography>
        )}

        {/* Content */}
        {filteredData.length === 0 ? (
          <Paper sx={{ textAlign: 'center', py: 6, borderRadius: 3 }}>
            <Typography variant="h6" color="text.secondary">
              No reviews found
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Try adjusting your filters or create a new review
            </Typography>
          </Paper>
        ) : viewMode === 'grid' ? (
          renderCardView()
        ) : (
          renderTableView()
        )}
      </Container>

      {/* Fullscreen Image Preview with transparent background */}
      <FullscreenImageDialog
        open={previewImageOpen}
        imageUrl={previewImageUrl}
        onClose={handleCloseImagePreview}
        title={previewImageTitle}
      />

      {/* Add/Edit Dialog - Redesigned with Sticky Header & Footer */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullScreen={isMobile}
        fullWidth
        className="sticky-dialog"
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '95vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Sticky Header */}
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #e8ecf0',
              bgcolor: '#fafbfc',
              py: 2,
              pr: 2,
              flexWrap: 'wrap',
              gap: 1,
              flexShrink: 0,
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              {`${operation} Review`}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          {/* Scrollable Content */}
          <DialogContent
            sx={{
              p: 3,
              overflow: 'auto',
              flex: '1 1 auto',
              '&:first-of-type': { pt: 3 },
            }}
          >
            <Grid container spacing={2}>
              {/* Image Upload - Left Column */}
              <Grid item xs={12} md={3}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Box
                    sx={{
                      border: `2px dashed ${errors.selectedImageFile ? '#d32f2f' : '#e0e0e0'}`,
                      borderRadius: 2,
                      p: 1,
                      width: '100%',
                      textAlign: 'center',
                      cursor: 'pointer',
                      '&:hover': { borderColor: 'primary.main' },
                    }}
                  >
                    <label htmlFor="fileInput" style={{ cursor: 'pointer' }}>
                      <img
                        src={selectedImageFile && imagePreview ? imagePreview : imagepreview}
                        alt="upload"
                        style={{
                          maxWidth: '100%',
                          maxHeight: '120px',
                          objectFit: 'contain',
                        }}
                      />
                      <input
                        id="fileInput"
                        type="file"
                        accept="image/jpeg, image/png, image/jpg"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </Box>
                  {errors.selectedImageFile && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {helperTexts.selectedImageFile}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {/* Form Fields - Right Column */}
              <Grid item xs={12} md={9}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Company Name"
                      name="CompanyName"
                      value={CompanyName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      fullWidth
                      size="small"
                      error={errors.CompanyName}
                      helperText={helperTexts.CompanyName}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="User Name"
                      name="UserName"
                      value={UserName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      fullWidth
                      size="small"
                      error={errors.UserName}
                      helperText={helperTexts.UserName}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Designation"
                      name="Designation"
                      value={Designation}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      fullWidth
                      size="small"
                      error={errors.Designation}
                      helperText={helperTexts.Designation}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Review Text */}
              <Grid item xs={12}>
                <TextField
                  label="Review"
                  name="Review"
                  multiline
                  rows={4}
                  value={Review}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  fullWidth
                  size="small"
                  error={errors.Review}
                  helperText={helperTexts.Review}
                  placeholder="Write the client's review here..."
                />
              </Grid>

              {/* Video URL */}
              <Grid item xs={12}>
                <TextField
                  label="Video URL (Optional)"
                  name="VideoURL"
                  value={VideoURL}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  fullWidth
                  size="small"
                  placeholder="https://www.youtube.com/watch?v=..."
                  error={errors.VideoURL}
                  helperText={helperTexts.VideoURL}
                />
              </Grid>
            </Grid>
          </DialogContent>

          {/* Sticky Footer with Switch and Buttons */}
          <DialogActions
            sx={{
              p: 2,
              borderTop: '1px solid #e8ecf0',
              bgcolor: '#fafbfc',
              gap: 2,
              flexShrink: 0,
              position: 'sticky',
              bottom: 0,
              zIndex: 10,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {/* Left side - Status Switch */}
            <Stack direction="row" spacing={2} alignItems="center">
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Switch
                      checked={ActiveStatus === 'Y'}
                      onChange={(e) =>
                        handleInputChange({ target: { name: 'ActiveStatus', value: e.target.checked ? 'Y' : 'N' } })
                      }
                      color="success"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={500}>
                      {ActiveStatus === 'Y' ? 'Active' : 'Inactive'}
                    </Typography>
                  }
                />
              </FormGroup>
            </Stack>

            {/* Right side - Action Buttons */}
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={handleClose} size="medium">
                Cancel
              </Button>
              <Button type="submit" variant="contained" size="medium">
                {operation === 'Add' ? 'Create Review' : 'Update Review'}
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDelete} onClose={handleCloseDelete} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Review?</DialogTitle>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="outlined" onClick={handleCloseDelete}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirmed}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loader */}
      <Dialog open={openLoader} fullWidth>
        <LinearProgress />
      </Dialog>
    </>
  );
};

export default ClientReviews;
